import type { Aircraft } from '@flightscanner/shared';
import type { AirlineInfo, AircraftTypeInfo } from '@flightscanner/shared';

/** Curated ICAO type designators that are unambiguously military airframes. */
export const EXPLICIT_MILITARY_TYPES = new Set([
  'A10', 'A29', 'ALCA', 'AV8B', 'BUC', 'EUFI', 'F4', 'F5', 'F14', 'F15', 'F16', 'F18', 'F22', 'F35',
  'HAWK', 'HUNT', 'J10', 'J11', 'J15', 'J16', 'J20', 'JAS39', 'JF17', 'L39', 'L159', 'M200', 'M346',
  'MIR2', 'MIR4', 'PC21', 'RAFA', 'SU27', 'SU30', 'SU35', 'T38', 'T45', 'T6', 'TOR', 'Y130',
  'A400', 'B1', 'B1B', 'B2', 'B52', 'C5', 'C5M', 'C17', 'C130', 'C135', 'E3', 'E3CF', 'E6', 'E7',
  'IL76', 'KC10', 'KC30', 'KC46', 'K35R', 'P8', 'T204', 'V22', 'RQ4', 'MQ9', 'MQ1', 'P3', 'B52H',
  'F117', 'MIG29', 'MIG31', 'SU34', 'SU24', 'SU25', 'M2000', 'TYPH', 'EF18',
]);

const MILITARY_OP =
  /air force|navy|army|military|marine corps|coast guard|luftwaffe|arm[eé]e de l'air|ej[eé]rcito|aeron[aá]utica|fuerza a[eé]rea|royal air|defen[cs]e (ministry|dept)|guardia nacional|national guard/i;

/** Common ADS-B ICAO hex blocks assigned to military/state operators. */
export function isMilHex(icao: string): boolean {
  const n = Number.parseInt(icao, 16);
  if (!Number.isFinite(n)) return false;
  // US Department of Defense block.
  if (n >= 0xae0000 && n <= 0xaeffff) return true;
  // UK / NATO-style blocks often seen on mil traffic.
  if (n >= 0x43c000 && n <= 0x43cfff) return true;
  if (n >= 0x3c0000 && n <= 0x3fffff) return true;
  return false;
}

/**
 * Military classification requires hard evidence — never a Cessna prefix heuristic.
 * Evidence: ADS-B mil flag, mil hex, known mil operator callsign, explicit mil type,
 * or operator string that clearly names a military service (when paired with mil hex/flag).
 */
export function hasMilitaryEvidence(
  ac: Pick<Aircraft, 'icao' | 'type' | 'isMilitary' | 'operator'>,
  airline: AirlineInfo | null,
  typeInfo: AircraftTypeInfo | null,
): boolean {
  if (ac.isMilitary) return true;
  if (isMilHex(ac.icao)) return true;
  if (airline?.isMilitary) return true;

  const type = (ac.type ?? '').toUpperCase();
  if (type && EXPLICIT_MILITARY_TYPES.has(type)) return true;
  if (typeInfo?.isMilitary && type && EXPLICIT_MILITARY_TYPES.has(typeInfo.icao)) return true;

  const op = ac.operator ?? '';
  if (MILITARY_OP.test(op) && (ac.isMilitary || isMilHex(ac.icao) || airline?.isMilitary)) return true;

  return false;
}
